import { useEffect, useRef, useState } from "react";
import html2pdf from "html2pdf.js";
import html2canvas from "html2canvas";
import { GetImages } from "../../../utils/GetImages";
import PropertiesList from "./common/PropertiesList";
import PnlStatementTable from "./PnlStatementTable";
import RentRollStatementTable from "./RentRollStatementTable";
import AddReport from "./AddReport";
import { useNavigate, useParams } from "react-router";
import {
  AnalyticsService,
  EntityType,
  OrganizationService,
  FilterEntityEnum,
  ReportTypesEnum,
} from "@propertelligent/client-api";
import apiHelper from "../../../utils/apiHelper";
import { UserLoginData } from "../../common/charts/Enums";
import { useError } from "../../../utils/context/ErrorContext";
import DateHelper from "../../../utils/DateHelper";
import DurationHeader from "./common/DurationHeader";
import AddButtonWithArrow from "../../firstTimeUserExperience/commonCom/AddButtonWithArrow";
import { useGeneratePdfContext } from "../../../utils/context/GeneratePdfContext";
import PrintLayout from "../../generatePdfDocument/printLayout";
import { Offcanvas } from "react-bootstrap";
import ReportPrintFrame from "./ReportPrintFrame";
import SendReminder from "../../tenants/SendReminder";
import ActionMenu from "../../../newComponents/actionMenus/action-menu";
import { TextCss } from "../../GlobalCss/TextCss";
import EmptyState from "../../../newComponents/emptystate";
import OrganizationNameHeader from "../../common/OrganizationNameHeader/OrganizationNameHeader";
import { usePermissions } from "../../../hooks/usePermissions";
import { PermissionGuard } from "../../../newComponents/PermissionGuard";
import NoAccess from "../../../pages/NoAccess";
import { AccessLevelEnum } from "@propertelligent/client-api";

const getMiddayUTCString = (date: Date | any) => {
  if (!date) return null;
  const d = new Date(date);
  if (isNaN(d.getTime())) return null;
  return new Date(
    Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 12, 0, 0),
  ).toISOString();
};

const ReportStatement = ({
  showButton,
  selectedValues,
  setSelectedValues = undefined,
  range,
  setRange = undefined,
  selectedOption,
  setSelectedOption = undefined,
  selectedIds,
  setSelectedIds = undefined,
  relativeDateString,
  setRelativeDateString = undefined,
  reportId = undefined,
  reportName,
  setReportName,

  Goback = () => {},
  setShowReportForm,
  setReportId = undefined,
}) => {
  const [show, setShow] = useState(false);
  const [organizationName, setOrganizationName] = useState("");
  const [organizationImage, setOrganizationImage] = useState("");
  const [isPrint, setISPrint] = useState(false);
  const [printUrl, setPrintUrl] = useState<string | null>(null);
  const [showEmail, setShowEmail] = useState(false);
  const [loading, setLoading] = useState(false);
  const {
    statementData,
    setStatementData,
    setReportStatementId,
    setOrgDetails,
    setSubHeaderName,
    subHeaderName,
    setFilesWithProgress,
  } = useGeneratePdfContext();
  const navigate = useNavigate();
  const contentRef = useRef(null);

  const check = usePermissions();
  const canWrite = check.canWrite("Reports");

  const headerRef = useRef(null);
  const reportType = parseInt(localStorage.getItem("ReportType"));
  const orgId = parseInt(sessionStorage.getItem(UserLoginData.organization_id));
  const dateFilterTypeId = DateHelper.getDateFilterType(relativeDateString);
  const { errors, errorMessages, setErrors, setErrorMessages, clearErrors } =
    useError();
  const handlePdfAction = (action: "download" | "print") => {
    setISPrint(true);
    setTimeout(async () => {
      const element = contentRef.current;
      const headerElement = headerRef.current;

      if (!element || !headerElement) return;

      // Capture Header as Image
      const headerCanvas = await html2canvas(headerElement, {
        scale: 3,
        useCORS: true,
      });
      const headerImgData = headerCanvas.toDataURL("image/png");

      const pageWidthPt = 842; // A4 Landscape width in points
      const marginPt = 20;
      const targetWidthPt = pageWidthPt - marginPt * 2;
      // Calculate height based on aspect ratio
      const headerHeightPt =
        (headerCanvas.height * targetWidthPt) / headerCanvas.width;

      const options = {
        margin: [headerHeightPt + 20, marginPt, 40, marginPt], // Dynamic top margin
        filename: `${reportName || "report"}.pdf`,
        image: { type: "jpeg", quality: 0.95 },
        html2canvas: { scale: 2 },
        jsPDF: {
          unit: "pt",
          format: "a4",
          orientation: "landscape",
        },
        pagebreak: { mode: ["avoid-all", "css", "legacy"] },
      };

      const pdfInstance = html2pdf()
        .from(element)
        .set(options)
        .toPdf()
        .get("pdf");

      pdfInstance
        .then((pdf) => {
          const totalPages = pdf.internal.getNumberOfPages();
          const pageWidth = pdf.internal.pageSize.getWidth();
          const pageHeight = pdf.internal.pageSize.getHeight();

          for (let i = 1; i <= totalPages; i++) {
            pdf.setPage(i);

            // Add Captured Header Image to Every Page with correct aspect ratio
            pdf.addImage(
              headerImgData,
              "PNG",
              marginPt, // X position
              10, // Y position
              targetWidthPt, // width
              headerHeightPt, // calculated height
            );

            // Add Page numbers
            pdf.setFontSize(10);
            pdf.setTextColor(150);
            pdf.text(
              `Page ${i} of ${totalPages}`,
              pageWidth - 70,
              pageHeight - 15,
            );

            // Add Generated date and time
            pdf.setFontSize(10);
            pdf.setTextColor(150);
            pdf.text(
              `Date Generated: ${new Date().toLocaleString()}`,
              pageWidth - 823,
              pageHeight - 15,
            );
          }

          if (action === "download") {
            pdf.save(`${reportName || "report"}.pdf`);
          } else if (action === "print") {
            // Print on the same page: ReportPrintFrame loads the PDF in an invisible frame and
            // opens the browser print dialog (which is the preview), instead of a new tab.
            pdf.autoPrint();
            setPrintUrl(URL.createObjectURL(pdf.output("blob")));
          }
        })
        .then(() => setISPrint(false));
    }, 200);
  };

  // Frees the PDF blob whenever a new print replaces it, or the page unmounts.
  useEffect(
    () => () => {
      if (printUrl) URL.revokeObjectURL(printUrl);
    },
    [printUrl],
  );

  const GeneratePdfForEmail = () => {
    setISPrint(true);
    setTimeout(async () => {
      const element = contentRef.current;
      const headerElement = headerRef.current;

      if (!element || !headerElement) {
        setISPrint(false);
        return;
      }

      // Capture Header as Image for Email PDF
      const headerCanvas = await html2canvas(headerElement, {
        scale: 3,
        useCORS: true,
      });
      const headerImgData = headerCanvas.toDataURL("image/png");

      const pageWidthIn = 8.5; // Letter Portrait width in inches
      const marginIn = 0.25;
      const targetWidthIn = pageWidthIn - marginIn * 2;
      const headerHeightIn =
        (headerCanvas.height * targetWidthIn) / headerCanvas.width;

      const options = {
        margin: [headerHeightIn + 0.2, marginIn, 0.5, marginIn], // Dynamic top margin
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 1.1 },
        jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
        pagebreak: { mode: ["avoid-all", "css", "legacy"] },
      };

      try {
        const pdfInstance = html2pdf().from(element).set(options);

        const pdfBlob = await pdfInstance
          .toPdf()
          .get("pdf")
          .then((pdf) => {
            const totalPages = pdf.internal.getNumberOfPages();
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();

            for (let i = 1; i <= totalPages; i++) {
              pdf.setPage(i);

              // Add Captured Header Image to Every Page (Units in Inches)
              pdf.addImage(
                headerImgData,
                "PNG",
                marginIn, // X position (inch)
                0.1, // Y position (inch)
                targetWidthIn, // width (inch)
                headerHeightIn, // calculated height (inch)
              );

              // Footer (Page numbers)
              pdf.setFontSize(10);
              pdf.setTextColor(150);
              pdf.text(
                `${i}/${totalPages}`,
                pageWidth - 0.75, // 0.75 inch from right
                pageHeight - 0.25, // 0.25 inch from bottom
              );
            }
          })
          .outputPdf("blob");

        const file = new File([pdfBlob], `${reportName || "report"}.pdf`, {
          type: "application/pdf",
        });

        setFilesWithProgress((prevFiles) => [
          ...prevFiles,
          { file, progress: 0 },
        ]);

        const interval = setInterval(() => {
          setFilesWithProgress((prevFiles) =>
            prevFiles.map((fileObj) => ({
              ...fileObj,
              progress: Math.min(fileObj.progress + 10, 100),
            })),
          );
        }, 500);

        setTimeout(() => {
          clearInterval(interval);
          setFilesWithProgress((prevFiles) =>
            prevFiles.map((fileObj) => ({
              ...fileObj,
              progress: 100,
            })),
          );
        }, 5000);
      } catch (error) {
        console.error(error);
      } finally {
        setTimeout(() => {
          setISPrint(false);
        }, 200);
      }
    }, 200);
  };

  useEffect(() => {
    if (reportId !== undefined) {
      return;
    } else {
      if (
        reportType === null ||
        isNaN(reportType) ||
        relativeDateString == ""
      ) {
        navigate(-1);
      }
    }
  }, [navigate, reportType, range, reportId, reportId]);

  const checkEmptyFields = (): boolean => {
    let hasError = false;
    clearErrors();
    if (reportName.trim() === "") {
      setErrors({ ...errors, reportName: true });
      setErrorMessages({
        ...errorMessages,
        reportName: "Report Name is required",
      });
      hasError = true;
    } else {
      setErrors({ ...errors, reportName: false });
      hasError = false;
    }
    return hasError;
  };

  const handleShow = () => {
    clearErrors();
    setShow(true);
  };
  const handleClose = () => {
    setShow(false);
    setReportName("");
    clearErrors();
  };

  const handleAddReport = (e) => {
    e.preventDefault();
    if (checkEmptyFields()) {
      return;
    }
    setLoading(true);
    const isCustom =
      relativeDateString === "Custom" || relativeDateString === "Custom Range";
    const reportObj = {
      id: reportId ? reportId : 0,
      entityId: orgId,
      entityTypeId: EntityType.Organization,
      name: reportName,
      description: "",
      reportTypeId: reportType,
      queryData: {
        filterEntityTypeId: Number(selectedOption),
        filterEntityIds:
          Number(selectedOption) == FilterEntityEnum.Organization
            ? [orgId]
            : selectedIds,
        dateFilter: {
          dateFilterTypeId: dateFilterTypeId,
          clientDate: DateHelper.getClientDate(),

          startDate: isCustom ? getMiddayUTCString(range[0].startDate) : null,
          endDate: isCustom ? getMiddayUTCString(range[0].endDate) : null,
          // startDate: relativeDateString === "Custom Range" || relativeDateString === "Custom" ? range[0].startDate : null,
          // endDate: relativeDateString === "Custom Range" || relativeDateString === "Custom" ? range[0].endDate : null,
        },
        entityNames:
          Number(selectedOption) == FilterEntityEnum.Organization
            ? []
            : selectedValues,
      },
    };
    const apiConfig = reportId
      ? {
          apiMethod: AnalyticsService.putApiReport,
          title: "Report Updated",
          successMessage: "Report Updated Successfully",
          failureMessage: "Unable to Update Report",
          apiParams: [reportId, reportObj],
        }
      : {
          apiMethod: AnalyticsService.postApiReport,
          title: "Report Saved",
          successMessage: "Report Added Successfully",
          failureMessage: "Unable to Add Report",
          apiParams: [reportObj],
        };

    apiHelper(
      apiConfig.apiMethod,
      {
        successMessage: apiConfig.successMessage,
        failureMessage: apiConfig.failureMessage,
        showNotification: true,
      },
      ...apiConfig.apiParams,
    )
      .then((response: any) => {
        if (response?.data?.success) {
          setLoading(false);
          setShow(false);
          if (setReportId && response?.data?.success?.id) {
            setReportId(response.data.success.id);
          }
          //    setReportName("");
          // navigate('/organization/reports')
        } else {
          setLoading(false);
        }
      })
      .catch((Err) => {
        setLoading(false);
        console.error(Err);
      });
  };

  const REPORT_NAME_MAX = 30;

  const inputOnchange = (e) => {
    const next = e.target.value ?? "";
    // Message only when the user attempts to exceed the limit (not at exactly 30).
    if (next.length > REPORT_NAME_MAX) {
      setReportName(next.slice(0, REPORT_NAME_MAX));
      setErrors({ ...errors, reportName: true });
      setErrorMessages({
        ...errorMessages,
        reportName: "Report name cannot exceed 30 characters",
      });
      return;
    }
    setReportName(next);
    setErrors({ ...errors, reportName: false });
    setErrorMessages({ ...errorMessages, reportName: "" });
  };

  useEffect(() => {
    if (reportId) {
      setReportStatementId(reportId);
    }
  }, [reportId]);

  const getStatementData = () => {
    const clientDate = DateHelper.getClientDate();

    const apiCall =
      reportType === ReportTypesEnum.Pnl
        ? AnalyticsService.getApiReportPnl
        : AnalyticsService.getApiReportRentRoll;

    apiHelper(apiCall, { showNotification: false }, reportId, clientDate)
      .then((response: any) => {
        const data = response?.data?.success;
        if (!data) return;

        setStatementData(data);

        // Set entity filters
        setSelectedValues(data?.queryData?.entityNames || []);
        setSelectedIds(data?.queryData?.filterEntityIds || []);
        const filterType = Number(data?.queryData?.filterEntityTypeId);
        if (!Number.isNaN(filterType)) {
          setSelectedOption(filterType);
        }
        // Set date filter
        const dateFilter = data?.queryData?.dateFilter;

        setRelativeDateString(dateFilter?.formatted?.DateFilterTypeId || "");

        setRange([
          {
            startDate: dateFilter?.startDate
              ? new Date(dateFilter.startDate)
              : new Date(),
            endDate: dateFilter?.endDate
              ? new Date(dateFilter.endDate)
              : new Date(),
            key: "selection",
          },
        ]);
      })
      .catch((Err) => {
        console.error(Err);
      });
  };

  useEffect(() => {
    if (reportId !== null && reportId !== undefined && reportId !== 0) {
      getStatementData();
    }
    return () => {
      // Clear sticky max-length / required errors when leaving this statement
      // (e.g. generate a different report) so they don't reappear on reopen.
      clearErrors();
    };
  }, []);

  const getHeaderInfo = () => {
    apiHelper(
      OrganizationService.getApiOrganizationHeaderInfo,
      { showNotification: false },
      orgId,
    )
      .then((response: any) => {
        setOrganizationName(response?.data?.success?.name);
        setOrganizationImage(
          response?.data?.success?.logoImageBase64 !== ""
            ? `data:image/*;base64,${response?.data?.success?.logoImageBase64}`
            : null,
        );
        setOrgDetails(response?.data?.success);
      })
      .catch((Err) => {
        console.error(Err);
      });
  };

  useEffect(() => {
    getHeaderInfo();
    setSubHeaderName(
      reportType === ReportTypesEnum.Pnl
        ? "Profit And Loss Statement"
        : "Rent Roll Statement",
    );
  }, [orgId]);

  const menuItems = [
    reportId &&
      canWrite && {
        label: "Edit",
        icon: GetImages.BlackPencil,
        action: handleShow,
      },
    reportId && canWrite && { isDivider: true },
    {
      label: "Download as Pdf",
      icon: GetImages.downloadIcon,
      action: () => handlePdfAction("download"),
    },
    { isDivider: true },
    {
      label: "Email",
      icon: GetImages.emailIcon,
      action: () => {
        GeneratePdfForEmail();
        setShowEmail(true);
      },
    },
    { isDivider: true },
    {
      label: "Print",
      icon: GetImages.Print,
      action: () => handlePdfAction("print"),
    },
  ].filter(Boolean);

  const isPnlEmpty = () => {
    return (
      !statementData?.incomeItems?.length &&
      !statementData?.expenseItems?.length
    );
  };
  const isRentRollEmpty = () => {
    if (!statementData?.rentRollItems?.length) return true;

    return statementData.rentRollItems.every(
      (item) =>
        (item.tenantName === "VACANT" || !item.tenantName) &&
        (item.rent === null || item.rent === 0),
    );
  };

  return (
    <PermissionGuard
      permission="Reports"
      level={AccessLevelEnum.ReadOnly}
      fallback={<NoAccess />}
    >
      <div className="Template_View_Main Report_statement">
        <>
          <div className="report_fixed_header ">
            <div className={`listing-card no-radius `}>
              <div className="d-flex justify-content-between align-items-center">
                <OrganizationNameHeader
                  OrgName={organizationName}
                  OrgImg={organizationImage}
                  Goback={Goback}
                  backPath={""}
                  SubHeaderName={
                    reportType === ReportTypesEnum.Pnl
                      ? "Profit And Loss Statement"
                      : "Rent Roll Statement"
                  }
                  element={
                    <div className="d-flex align-items-center gap-3">
                      {!reportId && canWrite && (
                        <AddButtonWithArrow
                          buttonname="Save Reports"
                          onClick={handleShow}
                          disabled={undefined}
                          iconDirection={undefined}
                          Icon={undefined}
                        />
                      )}

                      <ActionMenu
                        trigger={
                          <img
                            src={GetImages.dropdownIcon}
                            alt="Menu"
                            width={24}
                            height={24}
                            className="iconStyleWithWhiteFilter"
                          />
                        }
                        menuItems={menuItems}
                        Id={undefined}
                      />
                    </div>
                  }
                />
              </div>
              <div className="separator-horizontal" />

              <div className="duration_header">
                <div className="Duration_Header gap-2">
                  <div className="d-flex align-items-center gap-2 left_section ">
                    <p className={TextCss.grey_neutral.sub_heading_400_14}>
                      {" "}
                      Duration:
                    </p>
                    <p className={TextCss.grey_neutral.sub_heading_400_14}>
                      {statementData &&
                      statementData?.queryData?.dateFilter?.formatted
                        ?.DateFilterTypeId !== "Custom"
                        ? statementData?.queryData?.dateFilter?.formatted
                            ?.DateFilterTypeId
                        : statementData?.queryData?.dateFilter?.formatted
                              ?.DateFilterTypeId === "Custom"
                          ? `From  ${DateHelper.convertUTCtoDateTime(statementData?.queryData?.dateFilter?.startDate)} to ${DateHelper.convertUTCtoDateTime(statementData?.queryData?.dateFilter?.endDate)}`
                          : `From  ${DateHelper.convertUTCtoDateTime(range[0].startDate)}
                                                                   to ${DateHelper.convertUTCtoDateTime(range[0].endDate)}`}
                    </p>
                  </div>
                  {selectedValues?.length > 0 && <div className="divider" />}
                  {selectedValues?.length > 0 && (
                    <div className="d-flex align-items-center justify-content-center gap-3">
                      <p className={TextCss.grey_neutral.sub_heading_400_14}>
                        {selectedOption == FilterEntityEnum.Property
                          ? "Properties: "
                          : " Units: "}
                      </p>
                      <div className="d-flex flex-wrap gap-2">
                        {selectedValues &&
                          selectedValues.map((value, i) => (
                            <p
                              key={i}
                              className="selectedValueLabel normal_text"
                              title={value}
                            >
                              {value}
                            </p>
                          ))}
                      </div>{" "}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
        <div className={" p-2 h-100"}>
          {reportType === ReportTypesEnum.Pnl ? (
            // PNL
            isPnlEmpty() ? (
              <div className="emailRightSection emptySection">
                <EmptyState
                  heading="No Data Found"
                  icon={GetImages.PnlEmpty}
                  actionNode={
                    canWrite ? (
                      <AddButtonWithArrow
                        onClick={handleShow}
                        buttonname={reportId ? "Edit Filter" : "Save Report"}
                      />
                    ) : null
                  }
                  isBorder={false}
                />
              </div>
            ) : (
              <PnlStatementTable
                statementData={statementData}
                isPrint={isPrint}
              />
            )
          ) : // RENTROLL
          isRentRollEmpty() ? (
            <div className="emailRightSection emptySection">
              <EmptyState
                heading="No Data Found"
                icon={GetImages.PnlEmpty}
                actionNode={
                  canWrite ? (
                    <AddButtonWithArrow
                      onClick={handleShow}
                      buttonname={reportId ? "Edit Filter" : "Save Report"}
                    />
                  ) : null
                }
                isBorder={false}
              />
            </div>
          ) : (
            <RentRollStatementTable
              statementData={statementData}
              isPrint={isPrint}
            />
          )}
          {isPrint && (
            <div
              style={{ position: "absolute", top: "-9999px", left: "-9999px" }}
              data-theme="light"
            >
              <div
                ref={headerRef}
                style={{ width: "1000px", backgroundColor: "#fff" }}
              >
                <OrganizationNameHeader
                  OrgName={organizationName}
                  OrgImg={organizationImage}
                  Goback={undefined}
                  backPath=""
                  SubHeaderName={
                    reportType === ReportTypesEnum.Pnl
                      ? "Profit And Loss Statement"
                      : "Rent Roll Statement"
                  }
                />
              </div>
            </div>
          )}
          {isPrint && (
            <div style={{ position: "absolute", top: "-9999px", left: "-9999px", width: "100%" }}>
              <div ref={contentRef} className="pdf-container" data-theme="light">
              <PrintLayout isPrint={isPrint} hideHeader={true}>
                <div className="generatePdfView">
                  {isPrint && (
                    <div className="duration_header_print ">
                      <DurationHeader
                        startDate={range[0].startDate}
                        endDate={range[0].endDate}
                        statementData={statementData}
                      />
                      {selectedValues?.length > 0 && (
                        <PropertiesList
                          selectedValues={selectedValues}
                          selectedOption={selectedOption}
                        />
                      )}
                    </div>
                  )}
                  <div
                    className={
                      isPrint
                        ? "report_table_container_pdf"
                        : "report_table_container"
                    }
                  >
                    {reportType === ReportTypesEnum.Pnl ? (
                      <PnlStatementTable
                        statementData={statementData}
                        isPrint={isPrint}
                      />
                    ) : (
                      <RentRollStatementTable
                        statementData={statementData}
                        isPrint={isPrint}
                      />
                    )}
                  </div>
                </div>
              </PrintLayout>
            </div>
            </div>
          )}
        </div>
      </div>
      <AddReport
        title={reportId ? "Edit Report " : "Save Report"}
        show={show}
        handleClose={handleClose}
        selectedValues={selectedValues}
        selectedOption={selectedOption}
        range={range}
        reportName={reportName}
        handleAddReport={handleAddReport}
        inputOnchange={inputOnchange}
        relativeDateString={relativeDateString}
        loading={loading}
      />

      {printUrl && <ReportPrintFrame url={printUrl} />}

      <Offcanvas
        className="offcanvas serviceRequestForm"
        show={showEmail}
        onHide={() => {
          setShowEmail(false);
          setFilesWithProgress([]);
        }}
        placement={"end"}
        scroll={true}
      >
        <Offcanvas.Header className="canvasHeader" closeButton>
          <Offcanvas.Title>Email</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <div className="reportFilterForm h-100">
            <SendReminder />
          </div>
        </Offcanvas.Body>
      </Offcanvas>
    </PermissionGuard>
  );
};

export default ReportStatement;
